<script>
import { mdiMenuLeft, mdiMenuRight, mdiCircleSmall } from '@mdi/js'
import RefinerButton from '@v3c/components/RefinerButton.vue'

export default {
  name: 'CrvAppSideMenu',

  components: {
    RefinerButton,
  },

  props: {
    menuCollapsedLabel: {
      type: String,
      default: 'Hide Menu',
    },

    menuPinnedLabel: {
      type: String,
      default: 'Pin Menu',
    },

    isCollapsed: {
      type: Boolean,
      default: false,
    },

    sideMenu: {
      type: Array,
      default: () => [],
    },

    activeMenuId: {
      type: String,
      default: '',
    },

    refinerProjectId: {
      type: String,
      default: '',
      note: 'passthrough for optional refiner feedback form',
    },

    refinerMainFormId: {
      type: String,
      default: '',
      note: 'passthrough for optional refiner feedback form',
    },

    marketCountryCode: {
      type: String,
      default: 'NL',
      note: 'passthrough for optional refiner feedback form',
    },

    locale: {
      type: String,
      default: 'nl', // TODO: refiner's surveys currently default to 'nl', we prefer to make this 'en'
      note: 'passthrough for optional refiner feedback form',
    },

    locationNumber: {
      type: String,
      default: '???',
      note: 'passthrough for optional refiner feedback form',
    },

    appVersion: {
      type: String,
      default: '?.?.?',
      note: 'passthrough for optional refiner feedback form',
    },

    userRoles: {
      type: Array,
      default: () => [], // ['admin']
      note: 'passthrough for optional refiner feedback form',
    },

    userId: {
      type: String,
      default: '',
      note: 'passthrough for optional refiner feedback form',
    },

    userName: {
      type: String,
      default: '',
      note: 'passthrough for optional refiner feedback form',
    },

    userEmail: {
      type: String,
      default: '',
      note: 'passthrough for optional refiner feedback form',
    },
  },

  emits: ['navigate'],

  data() {
    return {
      showSideMenu: true,
      activeMenuItem: undefined,
      collapsed: this.isCollapsed,
      mdiMenuLeft,
      mdiMenuRight,
      mdiCircleSmall,
    }
  },

  computed: {
    prependCollapseIcon() {
      return this.collapsed ? mdiMenuRight : mdiMenuLeft
    },
    menuExpanded() {
      return !this.collapsed
    },
    collapseLabel() {
      return this.collapsed ? this.menuPinnedLabel : this.menuCollapsedLabel
    },
    showRefinerButton() {
      return this.refinerProjectId && this.refinerMainFormId
    },
  },

  methods: {
    handleClickCollapse() {
      this.collapsed = !this.collapsed
    },

    getIconName(iconName) {
      return iconName || mdiCircleSmall
    },

    handleClickMenuItem(item) {
      this.activeMenuItem = item
      this.$emit('navigate', item)

      this.$nextTick(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      })
    },

    getActive(key) {
      return key === this.activeMenuId
    },
  },
}
</script>

<template>
  <v-navigation-drawer
    v-model="showSideMenu"
    :rail="!menuExpanded"
    :expand-on-hover="!menuExpanded"
    width="240"
    color="base-brand-blue"
    location="left"
    class="crv-app-side-menu"
  >
    <slot />

    <v-list
      nav
      slim
      density="compact"
    >
      <template
        v-for="(item, index) in sideMenu"
        :key="item.key || `menu-item-${index}`"
      >
        <!-- Divider -->
        <v-divider
          v-if="item.isDivider"
          :key="`divider-${index}`"
          class="my-2"
        />

        <!-- Nested Group -->
        <v-list-group
          v-else-if="item.items"
          :value="item.key"
        >
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              :prepend-icon="getIconName(item.iconName)"
              :title="item.label"
              :class="{ 'active-item': getActive(item.key) }"
            />
          </template>

          <v-list-item
            v-for="child in item.items"
            :key="child.key"
            :title="child.label"
            :value="child.key"
            :prepend-icon="getIconName(child.iconName)"
            :class="{ 'active-item': getActive(child.key) }"
            @click="handleClickMenuItem(child)"
          />
        </v-list-group>

        <!-- Flat Item -->
        <v-list-item
          v-else
          :value="item.key"
          :prepend-icon="getIconName(item.iconName)"
          :title="item.label"
          :class="{ 'active-item': getActive(item.key) }"
          @click="handleClickMenuItem(item)"
        />
      </template>
    </v-list>

    <!-- Refiner & Collapse/Expand Button -->
    <template #append>
      <v-list
        nav
        class="pa-2"
      >
        <!-- Refiner Feedback -->
        <RefinerButton
          v-if="showRefinerButton"
          :project-id="refinerProjectId"
          :form-id="refinerMainFormId"
          :user-id="userId"
          :user-name="userName"
          :user-email="userEmail"
          :locale="locale"
          :route="$route.fullPath || routePath"
          :user-roles="userRoles"
          :app-version="appVersion"
          :market-country-code="marketCountryCode"
          :location-number="locationNumber"
          :is-mobile="$vuetify.display.smAndDown"
        />
        
        <!-- Collapse/Expand -->
        <v-list-item
          value="collapse"
          color="base-white"
          :prepend-icon="prependCollapseIcon"
          :title="collapseLabel"
          @click.stop="handleClickCollapse"
        />
      </v-list>
    </template>
  </v-navigation-drawer>
</template>


<style lang="scss" scoped>
@use '@v3c/assets/styles/variables.scss' as *;

::v-deep(.v-icon),
::v-deep(.v-list-item__prepend .v-icon),
::v-deep(.v-list-item__prepend i) {
  color: $base-white;
  opacity: 1;
}

::v-deep(.v-list-item-title) {
  color: $base-white;
}

.v-list-item--active,
.active-item {
  background-color: $base-brand-blue;
  color: $base-white;
  opacity: 1;
}

::v-deep(.v-list-item) {
  padding-left: 8px;
}

::v-deep(.v-list-group__items .v-list-item) {
  padding-left: 8px !important;
}

.crv-app-side-menu {
  border: none;

  ::v-deep(.v-list-item) {
    user-select: none;
    // prevent text selection on double click or accidental drag/selection.
  }
}
</style>
